"use client";
import { Fragment, type ReactNode, useRef, useState } from "react";
import { Bold, Heading2, Italic, Link2, List, ListOrdered } from "lucide-react";
import { webUrl } from "./discover";

/*
 * Simple formatting for service content, stored as plain text with light markup:
 *   **bold**   *italic*   ## Heading   - bullet   1. numbered   [text](https://link)
 * Rendered to React elements (never raw HTML), so stored content can't inject scripts.
 */

// ---------------------------------------------------------------- rendering

const INLINE = /(\*\*[^*\n]+?\*\*|\*[^*\s\n][^*\n]*?\*|\[[^\]\n]+\]\([^)\s]+\))/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4)
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2)
      return <em key={key}>{part.slice(1, -1)}</em>;
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const href = webUrl(link[2]);
      return href ? (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow" className="font-semibold underline">
          {link[1]}
        </a>
      ) : (
        <Fragment key={key}>{link[1]}</Fragment>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

type Block =
  | { type: "h"; text: string }
  | { type: "ul" | "ol"; items: string[] }
  | { type: "p"; lines: string[] };

function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  for (const raw of source.replace(/\r\n/g, "\n").split("\n")) {
    const line = raw.trimEnd();
    const last = blocks[blocks.length - 1];
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    const heading = line.match(/^#{1,3}\s+(.*)$/);

    if (!line.trim()) blocks.push({ type: "p", lines: [] }); // paragraph break
    else if (heading) blocks.push({ type: "h", text: heading[1] });
    else if (bullet) last?.type === "ul" ? last.items.push(bullet[1]) : blocks.push({ type: "ul", items: [bullet[1]] });
    else if (numbered) last?.type === "ol" ? last.items.push(numbered[1]) : blocks.push({ type: "ol", items: [numbered[1]] });
    else last?.type === "p" ? last.lines.push(line) : blocks.push({ type: "p", lines: [line] });
  }
  return blocks.filter((b) => b.type !== "p" || b.lines.length > 0);
}

export function RichText({ text, className = "" }: { text?: string | null; className?: string }) {
  if (!text?.trim()) return null;
  return (
    <div className={`space-y-3 ${className}`}>
      {parseBlocks(text).map((block, i) => {
        const key = `b${i}`;
        if (block.type === "h")
          return (
            <h3 key={key} className="pt-1 text-lg font-bold text-slate-900">
              {renderInline(block.text, key)}
            </h3>
          );
        if (block.type !== "p") {
          const ListTag = block.type;
          return (
            <ListTag key={key} className={`space-y-1 pl-5 ${block.type === "ul" ? "list-disc" : "list-decimal"}`}>
              {block.items.map((item, j) => (
                <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
              ))}
            </ListTag>
          );
        }
        return (
          <p key={key}>
            {block.lines.map((line, j) => (
              <Fragment key={`${key}-${j}`}>
                {j > 0 && <br />}
                {renderInline(line, `${key}-${j}`)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- editor

type Props = {
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  rows?: number;
  placeholder?: string;
};

export function RichTextEditor({ value, onChange, maxLength, rows = 10, placeholder }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState(false);

  // Apply an edit, then restore focus and the selection on the next frame.
  const apply = (next: string, selStart: number, selEnd: number) => {
    if (next.length > maxLength) return;
    onChange(next);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(selStart, selEnd);
    });
  };

  const wrap = (marker: string, fallback: string) => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: start, selectionEnd: end } = el;
    const selected = value.slice(start, end) || fallback;
    const next = value.slice(0, start) + marker + selected + marker + value.slice(end);
    apply(next, start + marker.length, start + marker.length + selected.length);
  };

  const prefixLines = (kind: "heading" | "bullet" | "numbered") => {
    const el = ref.current;
    if (!el) return;
    const lineStart = value.lastIndexOf("\n", el.selectionStart - 1) + 1;
    const nextBreak = value.indexOf("\n", el.selectionEnd);
    const lineEnd = nextBreak === -1 ? value.length : nextBreak;
    const lines = value.slice(lineStart, lineEnd).split("\n");
    const updated = lines
      .map((line, i) => {
        const clean = line.replace(/^(#{1,3}\s+|\s*[-*]\s+|\s*\d+[.)]\s+)/, "");
        if (kind === "heading") return `## ${clean}`;
        if (kind === "bullet") return `- ${clean}`;
        return `${i + 1}. ${clean}`;
      })
      .join("\n");
    const next = value.slice(0, lineStart) + updated + value.slice(lineEnd);
    apply(next, lineStart, lineStart + updated.length);
  };

  const link = () => {
    const el = ref.current;
    if (!el) return;
    const url = window.prompt("Link address (https://…)");
    if (!url || !webUrl(url)) return;
    const { selectionStart: start, selectionEnd: end } = el;
    const label = value.slice(start, end) || "link text";
    const inserted = `[${label}](${webUrl(url)})`;
    apply(value.slice(0, start) + inserted + value.slice(end), start + 1, start + 1 + label.length);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    const key = e.key.toLowerCase();
    if (key === "b") { e.preventDefault(); wrap("**", "bold text"); }
    if (key === "i") { e.preventDefault(); wrap("*", "italic text"); }
  };

  const tools = [
    { label: "Bold (Ctrl+B)", icon: Bold, run: () => wrap("**", "bold text") },
    { label: "Italic (Ctrl+I)", icon: Italic, run: () => wrap("*", "italic text") },
    { label: "Heading", icon: Heading2, run: () => prefixLines("heading") },
    { label: "Bullet list", icon: List, run: () => prefixLines("bullet") },
    { label: "Numbered list", icon: ListOrdered, run: () => prefixLines("numbered") },
    { label: "Link", icon: Link2, run: link },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        {tools.map(({ label, icon: Icon, run }) => (
          <button
            key={label}
            type="button"
            title={label}
            aria-label={label}
            disabled={preview}
            onMouseDown={(e) => e.preventDefault()} // keep the textarea selection
            onClick={run}
            className="rounded-md p-1.5 text-slate-600 hover:bg-white hover:text-slate-900 disabled:opacity-40"
          >
            <Icon size={16} />
          </button>
        ))}
        <div className="ml-auto flex rounded-lg bg-white p-0.5 text-xs font-semibold">
          {(["Write", "Preview"] as const).map((tab) => {
            const active = (tab === "Preview") === preview;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setPreview(tab === "Preview")}
                className={`rounded-md px-3 py-1 ${active ? "bg-[#0d5c75] text-white" : "text-slate-600"}`}
              >
                {tab}
              </button>
            );
          })}
        </div>
      </div>
      {preview ? (
        <div className="min-h-[12rem] px-4 py-3 text-sm leading-relaxed text-slate-700">
          {value.trim() ? <RichText text={value} /> : <p className="text-slate-400">Nothing to preview yet.</p>}
        </div>
      ) : (
        <textarea
          ref={ref}
          rows={rows}
          maxLength={maxLength}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="block w-full resize-y border-0 px-4 py-3 text-sm leading-relaxed outline-none focus:ring-0"
        />
      )}
    </div>
  );
}